import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, Loader2, Mail, MapPin, Phone, RefreshCw } from "lucide-react";
import { LOGO_URL } from "@/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [key, setKey] = useState("");
  const [authed, setAuthed] = useState(false);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async (adminKey) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/quotes`, { headers: { "x-admin-key": adminKey } });
      setLeads(res.data);
      setAuthed(true);
    } catch (e) {
      toast.error(e?.response?.status === 401 ? "Incorrect admin key." : "Failed to load leads.");
    } finally {
      setLoading(false);
    }
  };

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/quotes/${id}?status=${status}`, {}, { headers: { "x-admin-key": key } });
      setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
    } catch {
      toast.error("Could not update status.");
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen grid place-items-center bg-ink px-6">
        <div className="w-full max-w-sm rounded-2xl border border-white/10 bg-white/[0.03] p-8">
          <img src={LOGO_URL} alt="ClearCo" className="h-16 w-16 object-contain mx-auto" />
          <h1 className="mt-4 text-center font-display font-bold text-2xl text-white">Admin — Quote Requests</h1>
          <p className="mt-2 text-center text-slate-400 text-sm">Enter your admin key to view leads.</p>
          <div className="mt-6 relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
            <input
              data-testid="admin-key-input"
              type="password"
              className="w-full h-12 rounded-xl border border-white/15 bg-white/5 pl-10 pr-4 text-white placeholder:text-slate-500 focus:outline-none focus:border-brand"
              placeholder="Admin key"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load(key)}
            />
          </div>
          <button
            data-testid="admin-login-button"
            onClick={() => load(key)}
            disabled={loading || !key}
            className="mt-4 w-full h-12 rounded-xl bg-brand hover:bg-brand-hover text-white font-semibold transition disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 className="animate-spin" size={18} /> : "View Leads"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="bg-ink">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src={LOGO_URL} alt="ClearCo" className="h-10 w-10 object-contain" />
            <span className="font-display font-bold text-white">Quote Requests</span>
          </div>
          <button data-testid="admin-refresh-button" onClick={() => load(key)} className="flex items-center gap-2 text-sm text-slate-300 hover:text-white transition">
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <p className="text-slate-500 mb-6" data-testid="admin-lead-count">{leads.length} total request{leads.length !== 1 ? "s" : ""}</p>
        {leads.length === 0 ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-16 text-center text-slate-500">No quote requests yet.</div>
        ) : (
          <div className="space-y-4">
            {leads.map((l) => (
              <div key={l.id} data-testid={`admin-lead-${l.id}`} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <h3 className="font-display font-semibold text-lg text-slate-900">{l.first_name} {l.last_name}</h3>
                    <div className="mt-2 space-y-1 text-sm text-slate-600">
                      <p className="flex items-center gap-2"><MapPin size={14} className="text-brand" /> {l.address}</p>
                      <p className="flex items-center gap-2"><Mail size={14} className="text-brand" /> <a className="hover:text-brand" href={`mailto:${l.email}`}>{l.email}</a></p>
                      {l.phone && <p className="flex items-center gap-2"><Phone size={14} className="text-brand" /> {l.phone}</p>}
                    </div>
                    {l.service && <span className="inline-block mt-3 rounded-full bg-brand/10 text-brand text-xs font-semibold px-3 py-1">{l.service}</span>}
                    {l.message && <p className="mt-3 text-sm text-slate-500 italic">"{l.message}"</p>}
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-slate-400">{new Date(l.created_at).toLocaleString()}</p>
                    <select
                      data-testid={`admin-status-${l.id}`}
                      value={l.status}
                      onChange={(e) => setStatus(l.id, e.target.value)}
                      className="mt-2 rounded-lg border border-slate-200 text-sm px-3 py-1.5 focus:outline-none focus:border-brand"
                    >
                      <option value="new">New</option>
                      <option value="contacted">Contacted</option>
                      <option value="quoted">Quoted</option>
                      <option value="closed">Closed</option>
                    </select>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
